const STORAGE_KEYS = {
  soundEnabled: 'uptimio_notifications_sound_enabled',
  soundVolume: 'uptimio_notifications_sound_volume',
};

const DESKTOP_KEY = 'uptimio_notifications_desktop_enabled';

// Mapiramo "logičke" zvukove na stvarne fajlove iz public/notifications
export const SOUND_PATHS = {
  // uspešan login → toast zvuk (promenjeno jer info zvuk je za desktop notifikacije)
  loginSuccess: '/notifications/notification-sound-toast.mp3',
  // neuspešan login → error zvuk
  loginFailed: '/notifications/notification-sound-error.mp3',
  // nova notifikacija (zadaci / IT tiketi / itd.) → toast zvuk
  notification: '/notifications/notification-sound-toast.mp3',
  // desktop notifikacije → info zvuk
  desktopNotification: '/notifications/notification-sound-info.mp3',
  // globalno obaveštenje → poseban announcement zvuk
  announcement: '/notifications/announcement-sound.mp3',
} as const;

export type SoundKey = keyof typeof SOUND_PATHS;

export function isSoundEnabled(): boolean {
  const raw = localStorage.getItem(STORAGE_KEYS.soundEnabled);
  if (raw === null) return true; // default on
  return raw === 'true';
}

export function getSoundVolume(): number {
  const raw = localStorage.getItem(STORAGE_KEYS.soundVolume);
  if (!raw) return 50;
  const n = Number(raw);
  if (Number.isNaN(n)) return 50;
  return Math.min(100, Math.max(0, n));
}

// Fallback zvukovi po tipu
const FALLBACK_SOUNDS: Record<SoundKey, { freq: number; duration: number }> = {
  loginSuccess: { freq: 800, duration: 0.2 },
  loginFailed: { freq: 400, duration: 0.3 },
  notification: { freq: 600, duration: 0.15 },
  desktopNotification: { freq: 750, duration: 0.2 },
  announcement: { freq: 700, duration: 0.25 },
};

// Fallback: generiše jednostavan beep zvuk koristeći Web Audio API
async function playFallbackBeep(frequency: number, duration: number, volume: number) {
  try {
    const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioContextClass) {
      // eslint-disable-next-line no-console
      console.warn('[sound] Web Audio API not supported');
      return;
    }

    const audioContext = new AudioContextClass();
    
    // Pokušaj da resume ako je suspendovan (browser autoplay policy)
    if (audioContext.state === 'suspended') {
      try {
        await audioContext.resume();
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[sound] cannot resume audio context', err);
        return;
      }
    }

    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    oscillator.frequency.value = frequency;
    oscillator.type = 'sine';

    gainNode.gain.setValueAtTime(0, audioContext.currentTime);
    gainNode.gain.linearRampToValueAtTime(volume, audioContext.currentTime + 0.01);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + duration);

    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + duration);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[sound] fallback beep failed', err);
  }
}

export function playSound(key: SoundKey) {
  if (typeof window === 'undefined') return;
  if (!isSoundEnabled()) return;

  const volume = getSoundVolume() / 100;
  const src = SOUND_PATHS[key];

  // Debug log da vidimo da li se uopšte poziva
  // eslint-disable-next-line no-console
  console.debug('[sound] playSound', { key, src, volume });

  const audio = new Audio(src);
  audio.volume = volume;
  audio.preload = 'auto'; // Učitaj fajl unapred
  
  // Funkcija za pokretanje fallback zvuka
  const playFallback = () => {
    const fallback = FALLBACK_SOUNDS[key];
    if (fallback) {
      playFallbackBeep(fallback.freq, fallback.duration, volume).catch(() => {
        // Ignoriši greške iz fallback-a
      });
    }
  };

  // Proveri da li fajl postoji pre pokušaja reprodukcije
  audio.addEventListener('error', (e) => {
    // eslint-disable-next-line no-console
    console.warn('[sound] audio file error, using fallback beep', { key, src, error: e });
    playFallback();
  });

  // Pokušaj da reprodukujemo zvuk
  const playPromise = audio.play();
  
  if (playPromise !== undefined) {
    playPromise.catch((err) => {
      // eslint-disable-next-line no-console
      console.warn('[sound] cannot play audio, using fallback beep', { key, src, error: err });
      playFallback();
    });
  }
}

export function isDesktopNotificationsEnabled(): boolean {
  if (typeof window === 'undefined') return false;
  const raw = localStorage.getItem(DESKTOP_KEY);
  return raw === 'true';
}

export function showDesktopNotification(
  title: string,
  options?: NotificationOptions
): void {
  if (typeof window === 'undefined') return;
  if (!('Notification' in window)) return;
  if (!isDesktopNotificationsEnabled()) return;
  if (Notification.permission !== 'granted') return;
  
  // Reprodukuj zvuk za desktop notifikacije
  playSound('desktopNotification');
                                                                                                                                                                                                        
  // eslint-disable-next-line no-new
  new Notification(title, options);
}


